import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./Earnings.scss";
import {
  clearTopNavbar,
  setTopNavbar,
} from "../../Reducers/actions/navbarActions";
import Skeleton from "../../components/Skeleton/Skeleton";
import { PaymentSummaryRowSkeleton } from "../../components/Skeletons/FinanceListSkeletons";
import {
  getEarnings,
  setActiveTab,
} from "../../Reducers/actions/earningActions";
import Icon from "../../components/Icon/Icon";
import { getPaginatedPayments } from "../../Reducers/actions/paymentActions";
import PaymentSummary from "../PaymentHistory/PaymentSummary";
import { PAYMENT_TYPES } from "../../constants/globals";
import { fromCentsToPounds } from "../../../helpers/money";
import EmptyEarnings from "./EmptyEarnings";
export const tabs = [{ tab: 'all', icon: null }, { tab: 'purchase_physical', icon: "physical_sales_tab" }, { tab: 'purchase_digital', icon: "digital_sales_tab" }, { tab: "contribution", icon: "one_month_contribution_tab" }, { tab: "monthly_contribution", icon: "month_contribution_tab" }];
const tabNames = {
    purchase_digital: "digital sales",
    purchase_physical: "physical sales",
    contribution: "contributions",
    monthly_contribution: "monthly contribution",
    all: "earnings"
};

function EarningsScreenSkeleton() {
    return (
        <div className="earnings-container" role="status" aria-label="Loading earnings">
            <div className="summary-container px-16 py-24">
                <Skeleton width="140px" height="18px" borderRadius="8px" className="m-0 p-0" />
                <Skeleton width="200px" height="40px" borderRadius="10px" className="m-0 p-0 mt-8 mb-24" />
                <div className="analytics-box">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="box-info d-flex jcsb aic py-12">
                            <Skeleton width="55%" height="16px" borderRadius="8px" className="m-0 p-0" />
                            <Skeleton width="72px" height="16px" borderRadius="8px" className="m-0 p-0" />
                        </div>
                    ))}
                </div>
            </div>
            <div className="tabsa d-flex f-row jcc ais py-8 px-8">
                {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} width="44px" height="44px" borderRadius="10px" className="m-0 p-0 mx-4" />
                ))}
            </div>
            <div className="transactions-container px-16 py-24">
                {[1, 2, 3, 4].map((i) => (
                    <PaymentSummaryRowSkeleton key={i} />
                ))}
            </div>
        </div>
    );
}

const Earnings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { payments, paymentsLoading, earnings, earningsLoading, activeTab } = useSelector(
    (state) => ({
      paymentsLoading: state?.payments?.loading,
      payments: state?.payments?.payments,
      earningsLoading: state?.earnings?.earningsLoading,
      earnings: state?.earnings?.earnings,
      activeTab: state?.earnings?.activeTab
    })
  );

  const handleSetActiveTab = (tab) => {
    dispatch(setActiveTab(tab));
  };
    const getTabLabels = (tab) => tabNames[tab] || tab;

  useEffect(() => {
    dispatch(
      setTopNavbar({
        title: "Earnings",
        showBackArrow: true,
      })
    );
    dispatch(getPaginatedPayments(0, 10, null, false, true));
    dispatch(getEarnings());

    return () => {
      dispatch(clearTopNavbar());
    };
  }, []);

  const handleViewAllTransactions = () => {
    navigate("/earnings/transactions");
  };

  const filteredPayments = payments.filter(payment => {
    if (activeTab === 'all') return true;
    return payment.type === activeTab
  })

  const physicalEarnings =
    (earnings || [])?.find(
      (earning) => earning?.type === PAYMENT_TYPES.purchase_physical
    )?.total_amount || 0;
  const digitalEarnings =
    (earnings || [])?.find(
      (earning) => earning?.type === PAYMENT_TYPES.purchase_digital
    )?.total_amount || 0;
  const contributionEarnings =
    (earnings || [])?.find((earning) => earning?.type === PAYMENT_TYPES.contribution)
      ?.total_amount || 0;
  const monthlyContributionEarnings =
    (earnings || [])?.find(
      (earning) => earning?.type === PAYMENT_TYPES.monthly_contribution
    )?.total_amount || 0;

  const total = physicalEarnings + digitalEarnings + contributionEarnings + monthlyContributionEarnings;


  if (earningsLoading) {
    return <EarningsScreenSkeleton />;
  }

  if (total === 0 && !paymentsLoading && !earningsLoading && earnings?.length === 0) {
    return <EmptyEarnings />
  }

  return (
    <div className="earnings-container">
      <div className="summary-container px-16 py-24">
        <h3 className="revenue-title m-0">Total revenue</h3>
        <h1 className="revenue-total mb-24 mt-8">{fromCentsToPounds(total)}</h1>
        <div className="analytics-box">
          <div className="box-info top d-flex jcsb aic">
            <div className="d-flex jcsb aic">
              <div className="sub-title d-flex f-row">
                <div>
                  <Icon alias="box" color="#0A0F1E" width={24} height={24} />
                </div>
                Physical sales</div>

            </div>
            <div className="amount">{fromCentsToPounds(physicalEarnings)}</div>
          </div>
          <div className="box-info d-flex jcsb aic">
            <div className="d-flex jcsb aic">
              <div className="sub-title d-flex f-row aic jcc">
                <div >
                  <Icon alias="digital_sales" color="#0A0F1E" width={24} height={24} />
                </div>
                Digital sales</div>

            </div>
            <div className="amount">{fromCentsToPounds(digitalEarnings)}</div>
          </div>

          <div className="box-info d-flex jcsb aic">
            <div className="d-flex jcsb aic">
              <div className="sub-title d-flex f-row aic jcc">
                <div >
                  <Icon alias="one_time_contribution" color="#0A0F1E" width={24} height={24} />
                </div>
                Contribution</div>

            </div>
            <div className="amount">{fromCentsToPounds(contributionEarnings)}</div>
          </div>

          <div className="box-info bottom d-flex jcsb aic">
            <div className="d-flex jcsb aic">
              <div className="sub-title d-flex f-row aic jcc">
                <div >
                  <Icon alias="month_contribution" color="#0A0F1E" width={24} height={24} />
                </div>
                Monthly Contribution</div>

            </div>
            <div className="amount">{fromCentsToPounds(monthlyContributionEarnings)}</div>
          </div>

        </div>
      </div>
      <div className="tabsa d-flex f-row jcc ais ">
        {tabs.map((tab) => (
          (tab.tab === "all") ?
            <div
              className={`tabearning ${activeTab === tab.tab ? ' active' : ''}`}
              key={tab.tab}
              onClick={() => handleSetActiveTab(tab.tab)}
            >{tab.tab}</div> :
            <div className={`tabearningicon ${activeTab === tab.tab ? ' active' : ''}`}
            >
              <Icon
                key={tab.tab}
                alias={tab.icon}
                onClick={() => handleSetActiveTab(tab.tab)}
                color={activeTab === tab.tab ? "#FF3232" : "#0A0F1E"}
                height={56}
                style={{ paddingLeft: "16px" }}
              /></div>
        ))}
      </div>

      <div className="transactions-container px-16 py-24">

        {paymentsLoading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <PaymentSummaryRowSkeleton key={i} />
            ))}
          </>
        ) : filteredPayments.length == 0 ? (<div className="d-flex f-column aic jcc px-16 ">
                    <p className="fs-xs fg-dark fw-bold mt-40 jcc aic">No {getTabLabels(activeTab)} yet</p>
                    <p className="fg-grey7 fs-xs fw-medium ">Your earnings from {getTabLabels(activeTab)} will appear here</p>
                </div>) :(
          filteredPayments?.map((payment) => <PaymentSummary payment={payment} />)
        )}


      </div>
              <div className="d-flex f-row jcc aic ">

                <button className="viewAllbutton"
                onClick={handleViewAllTransactions}
                >View all</button>

              </div>

    </div>
  );
};

export default Earnings;
